import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, LinearProgress, Link, Paper, Typography } from '@mui/material';
import { ArrowBack, Check } from '@mui/icons-material';

export const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

export const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null);

export const dateTime = (iso) => (iso
    ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
    : null);

const STATUS = {
    setup: { label: 'Setup', color: '#4B5565', background: '#EDF0F2' },
    open: { label: 'Open', color: '#1F6B31', background: '#E6F4EA' },
    closed: { label: 'Closed', color: '#4B5565', background: '#EDF0F2' },
};

export const RunStatusChip = ({ status }) => {
    const style = STATUS[status] || STATUS.setup;
    return <Chip size="small" label={style.label} sx={{ borderRadius: 999, fontWeight: 600, color: style.color, bgcolor: style.background }} />;
};

export const BackToRuns = () => (
    <Link component={RouterLink} to="/runs" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', fontWeight: 600 }}>
        <ArrowBack sx={{ fontSize: 16 }} /> Runs
    </Link>
);

// Upload · Check · Map slots · Assign lessons (wf-run-import, wf-run-assign)
export const IMPORT_STEPS = ['Upload', 'Check', 'Map slots', 'Assign lessons'];

export const WizardSteps = ({ steps = IMPORT_STEPS, active }) => (
    <Box component="ol" aria-label="Steps" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap', listStyle: 'none', p: 0, m: 0, mt: 2, mb: 3 }}>
        {steps.map((label, i) => {
            const done = i < active;
            const current = i === active;
            return (
                <Box component="li" key={label} aria-current={current ? 'step' : undefined} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {i > 0 && <Box aria-hidden sx={{ width: { xs: 12, sm: 28 }, height: 1, bgcolor: 'divider' }} />}
                    <Box
                        aria-hidden
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: done || current ? '#FFFFFF' : 'text.secondary',
                            bgcolor: done ? 'success.main' : current ? 'primary.main' : 'action.selected',
                            transition: 'background-color 200ms',
                        }}
                    >
                        {done ? <Check sx={{ fontSize: 16 }} /> : i + 1}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: current ? 700 : 500, color: done ? 'success.dark' : current ? 'text.primary' : 'text.secondary' }}>
                        {label}
                        {done && <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}> (done)</Box>}
                    </Typography>
                </Box>
            );
        })}
    </Box>
);

export const Card = ({ title, children, sx, ...rest }) => (
    <Paper component="section" aria-label={typeof title === 'string' ? title : undefined} sx={{ p: { xs: 2, sm: 3 }, ...sx }} {...rest}>
        {title && <Typography variant="overline" component="h2" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{title}</Typography>}
        {children}
    </Paper>
);

// A thin bar for "x of y" counts; green for final labels
export const Bar = ({ value, total, color = 'primary', width = 240 }) => (
    <LinearProgress
        variant="determinate"
        value={total ? Math.min(100, Math.round((value / total) * 100)) : 0}
        color={color}
        aria-label={`${fmt(value)} of ${fmt(total)}`}
        sx={{ width: '100%', maxWidth: width, height: 6, borderRadius: 999, bgcolor: 'action.selected', '& .MuiLinearProgress-bar': { borderRadius: 999 } }}
    />
);

export const PageTitle = ({ children, aside }) => (
    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mt: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>{children}</Box>
        {aside}
    </Box>
);
